import React, { useState } from 'react';
import { Search, UserPlus, Check, Calendar, Clock } from 'lucide-react';
import { clientService } from '../../services/clientService';
import { WizardStepProps } from '../../types/bookingWizard.types';
import { Client } from '../../types/client.types';

type SearchMode = 'phone' | 'name';

export const ClientSearchStep: React.FC<WizardStepProps> = ({
    data,
    onUpdate,
    onNext
}) => {
    const [searchMode, setSearchMode] = useState<SearchMode>('phone');
    const [searchPhone, setSearchPhone] = useState('');
    const [searchName, setSearchName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showNewClientForm, setShowNewClientForm] = useState(false);
    const [newClientName, setNewClientName] = useState('');
    const [searchResults, setSearchResults] = useState<Client[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const formatPhone = (value: string): string => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 10) {
            return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
        }
        return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
    };

    const formatDateTime = () => {
        if (!data.date || !data.time) return '';
        const date = new Date(`${data.date}T${data.time}:00`);
        const dateStr = date.toLocaleDateString('pt-BR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        return `${dateStr} às ${data.time}`;
    };

    const handleSearch = async () => {
        if (searchMode === 'phone' && !searchPhone) return;
        if (searchMode === 'name' && !searchName) return;

        setLoading(true);
        setError('');
        setSearchResults([]);
        setHasSearched(true);

        try {
            if (searchMode === 'phone') {
                const phoneDigits = searchPhone.replace(/\D/g, '');
                const result = await clientService.searchByPhone(phoneDigits);

                if (result.exists) {
                    if (result.client) {
                        onUpdate({ client: result.client });
                    } else if (result.clients && result.clients.length > 0) {
                        if (result.clients.length === 1) {
                            onUpdate({ client: result.clients[0] });
                        } else {
                            setSearchResults(result.clients);
                        }
                    } else {
                        setShowNewClientForm(true);
                    }
                } else {
                    setShowNewClientForm(true);
                }
            } else {
                const result = await clientService.searchByName(searchName);

                if (result.exists && result.clients && result.clients.length > 0) {
                    if (result.clients.length === 1) {
                        onUpdate({ client: result.clients[0] });
                    } else {
                        setSearchResults(result.clients);
                    }
                } else {
                    setShowNewClientForm(true);
                }
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erro ao buscar cliente');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectClient = (client: Client) => {
        onUpdate({ client });
        setSearchResults([]);
    };

    const handleCreateClient = async () => {
        if (!newClientName || !searchPhone) return;

        setLoading(true);
        try {
            // Remove formatting and send only digits
            const phoneDigits = searchPhone.replace(/\D/g, '');
            const result = await clientService.quickCreate({
                name: newClientName,
                phone: phoneDigits
            });
            onUpdate({ client: result.client });
            setShowNewClientForm(false);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erro ao criar cliente');
        } finally {
            setLoading(false);
        }
    };

    const handleChangeClient = () => {
        onUpdate({ client: null });
        setShowNewClientForm(false);
        setSearchPhone('');
        setSearchName('');
        setNewClientName('');
        setSearchResults([]);
        setHasSearched(false);
    };

    const handleChangeSearchMode = (mode: SearchMode) => {
        setSearchMode(mode);
        setError('');
        setSearchResults([]);
        setShowNewClientForm(false);
        setHasSearched(false);
    };

    if (data.client) {
        return (
            <div className="step-container">
                <div className="appointment-info">
                    <div className="info-item">
                        <Calendar size={18} />
                        <span>{formatDateTime()}</span>
                    </div>
                </div>
                <div className="client-selected">
                    <div className="client-info">
                        <div className="client-avatar">
                            {data.client.photo ? (
                                <img src={data.client.photo} alt={data.client.name} />
                            ) : (
                                <span>{data.client.name.charAt(0)}</span>
                            )}
                        </div>
                        <div className="client-details">
                            <h3>{data.client.name}</h3>
                            <p>{formatPhone(data.client.phone)}</p>
                        </div>
                        <Check className="check-icon" />
                    </div>
                    <button className="btn-secondary" onClick={handleChangeClient}>
                        Trocar Cliente
                    </button>
                    <button className="btn-primary" onClick={onNext}>
                        Continuar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="step-container">
            <div className="appointment-info">
                <div className="info-item">
                    <Calendar size={18} />
                    <span>{formatDateTime()}</span>
                </div>
            </div>

            {!showNewClientForm && (
                <>
                    <h3>Buscar Cliente</h3>

                    <div className="search-mode-toggle">
                        <button
                            className={`mode-btn ${searchMode === 'phone' ? 'active' : ''}`}
                            onClick={() => handleChangeSearchMode('phone')}
                        >
                            <Clock size={16} />
                            Por Telefone
                        </button>
                        <button
                            className={`mode-btn ${searchMode === 'name' ? 'active' : ''}`}
                            onClick={() => handleChangeSearchMode('name')}
                        >
                            <Search size={16} />
                            Por Nome
                        </button>
                    </div>

                    <p>
                        {searchMode === 'phone'
                            ? 'Digite o número de telefone do cliente'
                            : 'Digite o nome do cliente'
                        }
                    </p>

                    <div className="search-box">
                        {searchMode === 'phone' ? (
                            <input
                                type="text"
                                value={searchPhone}
                                onChange={(e) => setSearchPhone(formatPhone(e.target.value))}
                                placeholder="(99) 99999-9999"
                                maxLength={15}
                            />
                        ) : (
                            <input
                                type="text"
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                                placeholder="Nome do cliente"
                            />
                        )}
                        <button
                            className="btn-primary"
                            onClick={handleSearch}
                            disabled={loading || (searchMode === 'phone' ? searchPhone.length < 3 : searchName.length < 2)}
                        >
                            <Search size={20} />
                            {loading ? 'Buscando...' : 'Buscar'}
                        </button>
                    </div>
                </>
            )}

            {!showNewClientForm && searchResults.length > 0 && (
                <div className="search-results">
                    <p className="results-title">Selecione um cliente:</p>
                    {searchResults.map((client) => (
                        <div
                            key={client.id}
                            className="result-item"
                            onClick={() => handleSelectClient(client)}
                        >
                            <div className="client-avatar small">
                                {client.photo ? (
                                    <img src={client.photo} alt={client.name} />
                                ) : (
                                    <span>{client.name.charAt(0)}</span>
                                )}
                            </div>
                            <div className="result-info">
                                <span className="result-name">{client.name}</span>
                                <span className="result-phone">{formatPhone(client.phone)}</span>
                            </div>
                        </div>
                    ))}
                    <div className="register-new-btn-container">
                        <button
                            className="btn-secondary"
                            onClick={() => {
                                setShowNewClientForm(true);
                                setSearchResults([]);
                            }}
                        >
                            <UserPlus size={18} />
                            Cadastrar Novo Cliente
                        </button>
                    </div>
                </div>
            )}

            {showNewClientForm && (
                <div className="new-client-form">
                    <h3>Cadastrar Novo Cliente</h3>
                    <input
                        type="text"
                        value={newClientName}
                        onChange={(e) => setNewClientName(e.target.value)}
                        placeholder="Nome completo do cliente"
                    />
                    {searchMode === 'phone' ? (
                        <div className="phone-display">
                            <label>Telefone:</label>
                            <span>{formatPhone(searchPhone)}</span>
                        </div>
                    ) : (
                        <input
                            type="text"
                            value={searchPhone}
                            onChange={(e) => setSearchPhone(formatPhone(e.target.value))}
                            placeholder="Telefone (99) 99999-9999"
                            maxLength={15}
                        />
                    )}
                    <div className="form-actions">
                        <button
                            className="btn-secondary"
                            onClick={() => setShowNewClientForm(false)}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn-primary"
                            onClick={handleCreateClient}
                            disabled={loading || !newClientName || searchPhone.length < 14}
                        >
                            <UserPlus size={20} />
                            Cadastrar
                        </button>
                    </div>
                </div>
            )}

            {hasSearched && searchResults.length === 0 && !showNewClientForm && (
                <div className="no-results">
                    <p className="info-text">Nenhum cliente encontrado.</p>
                    <button
                        className="btn-secondary"
                        onClick={() => setShowNewClientForm(true)}
                    >
                        <UserPlus size={18} />
                        Cadastrar Novo Cliente
                    </button>
                </div>
            )}

            {error && <div className="error-message">{error}</div>}
        </div>
    );
};

export default ClientSearchStep;
